import React, { Component } from "react";
import type { Location, NavigateFunction } from "react-router-dom";
import { getAuthRequestUrl, getCalendarAll, validateAuthToken } from "../../api";
import type { CalendarRecord } from "../../api/types";
import { formatAuthError } from "../../auth/formatAuthError";
import { getOAuthRedirectUriForCalendar } from "../../auth/oauthRedirect";
import { getCompanionSession, saveCompanionSession, setUserProfile } from "../../auth/session";
import { getDashboardCalendarPath } from "../../utils/dashboardPaths";
import { isMobileUserAgent } from "../../utils/isMobileUserAgent";
import "./CalendarPage.scss";

let calendarOAuthCodeInflight: string | null = null;

function hasLinkedCalendars(calendars: CalendarRecord[] | undefined): boolean {
  if (!calendars?.length) {
    return false;
  }
  return calendars.some((rec) => Object.values(rec).some(Boolean));
}

interface CalendarPageProps {
  navigate: NavigateFunction;
  location: Location;
}

interface CalendarPageState {
  isMobile: boolean;
  calendars: CalendarRecord[] | null;
  listLoading: boolean;
  listError: string | null;
  oauthCallbackError: string | null;
  isStartingCalendarAuth: boolean;
  isProcessingOAuthCallback: boolean;
}

class CalendarPage extends Component<CalendarPageProps, CalendarPageState> {
  constructor(props: CalendarPageProps) {
    super(props);
    this.state = {
      isMobile: isMobileUserAgent(),
      calendars: null,
      listLoading: true,
      listError: null,
      oauthCallbackError: null,
      isStartingCalendarAuth: false,
      isProcessingOAuthCallback: false,
    };
  }

  componentDidMount(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.handleCalendarOAuthRedirect();
    await this.loadCalendars();
  }

  private async loadCalendars(): Promise<void> {
    const session = getCompanionSession();
    if (!session) {
      this.setState({
        listLoading: false,
        listError: "Not signed in.",
        calendars: null,
      });
      return;
    }

    this.setState({ listLoading: true, listError: null });

    try {
      const resp = await getCalendarAll({
        xSid: session.sid,
        xEmail: session.email,
      });
      if (resp.status !== "SUCCESS") {
        this.setState({
          listLoading: false,
          listError: resp.error ?? "Could not load calendars.",
          calendars: [],
        });
        return;
      }
      this.setState({
        listLoading: false,
        calendars: resp.calendars ?? [],
      });
    } catch (err: unknown) {
      this.setState({
        listLoading: false,
        listError: formatAuthError(err),
        calendars: [],
      });
    }
  }

  private async handleCalendarOAuthRedirect(): Promise<void> {
    const { location, navigate } = this.props;
    const params = new URLSearchParams(location.search);
    const oauthError = params.get("error");
    const code = params.get("code");

    if (oauthError) {
      const desc = params.get("error_description") ?? oauthError;
      this.setState({ oauthCallbackError: desc });
      navigate(getDashboardCalendarPath(), { replace: true });
      return;
    }

    if (!code) {
      return;
    }

    if (calendarOAuthCodeInflight === code) {
      return;
    }
    calendarOAuthCodeInflight = code;

    this.setState({ isProcessingOAuthCallback: true, oauthCallbackError: null });

    try {
      const resp = await validateAuthToken({
        token: code,
        scope: "calendar",
        redirect_uri: getOAuthRedirectUriForCalendar(),
      });
      if (resp.status !== "SUCCESS" || !resp.sid) {
        const msg = resp.error ?? "Calendar authorization failed.";
        this.setState({
          oauthCallbackError: msg,
          isProcessingOAuthCallback: false,
        });
        navigate(getDashboardCalendarPath(), { replace: true });
        calendarOAuthCodeInflight = null;
        return;
      }
      const email = resp.email_id;
      if (!email) {
        this.setState({
          oauthCallbackError: "Missing email in auth response.",
          isProcessingOAuthCallback: false,
        });
        navigate(getDashboardCalendarPath(), { replace: true });
        calendarOAuthCodeInflight = null;
        return;
      }
      saveCompanionSession({ sid: resp.sid, email });
      setUserProfile({
        agent_email: resp.agent_email,
        agent_name: resp.agent_name,
        name: resp.user_name,
        pfp: resp.pfp,
        email_id: resp.email_id,
      });
      calendarOAuthCodeInflight = null;
      this.setState({ isProcessingOAuthCallback: false });
      navigate(getDashboardCalendarPath(), { replace: true });
    } catch (err: unknown) {
      this.setState({
        oauthCallbackError: formatAuthError(err),
        isProcessingOAuthCallback: false,
      });
      navigate(getDashboardCalendarPath(), { replace: true });
      calendarOAuthCodeInflight = null;
    }
  }

  private startCalendarLink(): Promise<void> {
    if (this.state.isStartingCalendarAuth) {
      return Promise.resolve();
    }

    const session = getCompanionSession();
    if (!session) {
      this.setState({ listError: "Not signed in." });
      return Promise.resolve();
    }

    this.setState({ isStartingCalendarAuth: true, listError: null, oauthCallbackError: null });
    return getAuthRequestUrl({
      scope: "calendar",
      redirectUri: getOAuthRedirectUriForCalendar(),
      xEmail: session.email,
    })
      .then((resp) => {
        if (resp.status === "FAIL") {
          throw new Error(resp.error ?? "Could not start calendar linking.");
        }
        if (!resp.request_url) {
          throw new Error("Missing request_url from auth_request_url response");
        }
        window.location.href = resp.request_url;
      })
      .catch((err: unknown) => {
        this.setState({
          isStartingCalendarAuth: false,
          listError: formatAuthError(err),
        });
      });
  }

  render(): React.JSX.Element {
    const {
      isMobile,
      calendars,
      listLoading,
      listError,
      oauthCallbackError,
      isStartingCalendarAuth,
      isProcessingOAuthCallback,
    } = this.state;

    const linked = hasLinkedCalendars(calendars ?? undefined);
    const busy = isStartingCalendarAuth || isProcessingOAuthCallback;
    const rootClass = isMobile ? "calendar-page_mobile" : "calendar-page";

    return (
      <div className={rootClass}>

        {oauthCallbackError ? (
          <p className="calendar-page-error" role="alert">
            {oauthCallbackError}
          </p>
        ) : null}

        <section className="calendar-page-cards" aria-label="Linked calendars">
          <article className="calendar-page-card">

            {listLoading ? (
              <p className="calendar-page-muted">Loading…</p>
            ) : listError ? (
              <p className="calendar-page-error" role="alert">
                {listError}
              </p>
            ) : linked ? (
              <>
                <h2 className="calendar-page-card-title">Connected Calendar</h2>
                <div className="calendar-page-card-subs">
                  This is the calendar your assistant can access. It uses this to check availability, schedule meetings, and create events on your behalf.
                </div>
                <ul className="calendar-page-list calendar-page-card-body">
                  {(calendars ?? []).map((rec, idx) =>
                    Object.entries(rec).map(([email, synced]) => (
                      <li key={`${idx}-${email}`}>
                        {email} — synced: {synced ? <span>yes</span> : "no"}
                      </li>
                    )),
                  )}
                </ul>
              </>
            ) : (
              <>
                <h2 className="calendar-page-card-title">No Calendar Connected</h2>
                <div className="calendar-page-card-subs">Connect a calendar to give your assistant access to check availability, schedule meetings, and create events on your behalf.</div>
                <button
                  type="button"
                  className="calendar-page-link-google"
                  onClick={() => {
                    void this.startCalendarLink();
                  }}
                  disabled={busy}
                >
                  {busy ? "Please wait…" : "Link Google Calendar"}
                </button>
              </>
            )}
          </article>
        </section>
      </div>
    );
  }
}

export default CalendarPage;
